import type { FC } from "react";
import { useCallback, useRef } from "react";

import type { PopperProps } from "@mui/material";
import { Fade } from "@mui/material";
import clsx from "clsx";
import type { positionValues } from "react-custom-scrollbars-2";
import { Scrollbars } from "react-custom-scrollbars-2";

import { Button, Text } from "shared/uikit";
import { ButtonSizesEnum, TextVariantsEnum } from "shared/uikit/types";

import { NavbarDialogWrapper } from "./style";

const NavbarDialog: FC<{
	title: string;
	className?: string;
	children: React.ReactNode;
	maxHeight?: number;
	filters?: {
		name: string;
		onClick: () => void;
		active?: boolean;
	}[];
	hideFiltersBorder?: boolean;
	popoverProps: PopperProps;
	ref?: React.Ref<HTMLDivElement>;
	onScrollDown?: () => void;
	bottomAction?: {
		text: string;
		onClick: () => void;
	};
	autoHeightMax?: number;
}> = ({
	title,
	children,
	className,
	maxHeight,
	filters,
	hideFiltersBorder,
	popoverProps,
	ref,
	onScrollDown,
	bottomAction,
	autoHeightMax
}) => {
	const scrollbarContainerRef = useRef<Scrollbars | null>(null);

	const scrollToTop = useCallback(() => {
		scrollbarContainerRef.current?.scrollToTop();
	}, []);

	const handleScrollDown = useCallback(
		(values: positionValues) => {
			if (values.top >= 0.9) {
				onScrollDown && onScrollDown();
			}
		},
		[onScrollDown]
	);

	return (
		<NavbarDialogWrapper
			{...popoverProps}
			className={clsx("show", className)}
			maxHeight={maxHeight}
			ref={ref}
			transition
		>
			{({ TransitionProps }) => (
				<Fade {...TransitionProps} timeout={450}>
					<div>
						{title && (
							<Text variants={TextVariantsEnum.H6} className="p-4 pb-3 border-b border-gray-100">
								{title}
							</Text>
						)}
						{!!filters?.length && (
							<div
								className={clsx(
									"flex items-center flex-wrap gap-2 py-3 px-4",
									!hideFiltersBorder && "border-b border-gray-100"
								)}
							>
								{filters.map((filter, i) => (
									<Button
										key={i}
										size={ButtonSizesEnum.XS}
										secondary
										onClick={() => {
											scrollToTop();
											filter.onClick();
										}}
										active={filter.active}
									>
										{filter.name}
									</Button>
								))}
							</div>
						)}
						<Scrollbars
							ref={scrollbarContainerRef}
							onUpdate={handleScrollDown}
							autoHeight
							autoHeightMin={50}
							autoHeightMax={autoHeightMax || 400}
							autoHide
						>
							{children}
						</Scrollbars>
						{bottomAction && (
							<div className="p-4 border-t border-gray-100">
								<Button size={ButtonSizesEnum.SM} className="w-full" onClick={bottomAction.onClick} fixed secondary>
									{bottomAction.text}
								</Button>
							</div>
						)}
					</div>
				</Fade>
			)}
		</NavbarDialogWrapper>
	);
};

export default NavbarDialog;
